import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  Tabs, Tab, Divider, MenuItem, Grid, Paper, FormControlLabel, Switch,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import TableChartIcon from '@mui/icons-material/TableChart';
import PaletteIcon from '@mui/icons-material/Palette';
import { TableColumnsSettings } from '../components/settings/TableColumnsSettings';
import { settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useAppTheme } from '../contexts/ThemeContext';
import { ImageUploadField } from '../components/ui/ImageUploadField';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../theme/appTheme';
import { normalizeCountryCode } from '../i18n/localeConfig';
import SettingsIcon from '@mui/icons-material/Settings';
import { PageHeader } from '../components/ui/PageHeader';
import { platformTenantUrl, PLATFORM_DOMAIN } from '../constants/brand';
import { RegionalSettingsPanel } from '../components/settings/RegionalSettingsPanel';
import { ThemePresetPicker } from '../components/settings/ThemePresetPicker';

const HEX_FALLBACK = {
  primaryColor: '#1976d2',
  secondaryColor: '#dc004e',
  sidebarColor: '#1a237e',
  accentColor: '#42a5f5',
  tableColor: '#1a237e',
  formHeaderColor: '#1976d2',
} as const;

function toHexColor(value: string | undefined, fallback: string): string {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  return fallback;
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || fallback;
}

const ColorField: React.FC<{
  label: string;
  value: string;
  onChange: (hex: string) => void;
  helperText?: string;
}> = ({ label, value, onChange, helperText }) => {
  const hex = toHexColor(value, '#6221d6');
  return (
    <Box>
      <Typography fontSize={12} fontWeight={700} mb={0.75}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box
          component="input"
          type="color"
          value={hex}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          sx={{
            width: 48, height: 40, border: '1px solid #e2e8f0', borderRadius: 1,
            p: 0.25, cursor: 'pointer', bgcolor: '#fff', flexShrink: 0,
          }}
        />
        <TextField
          size="small"
          value={hex}
          onChange={(e) => onChange(toHexColor(e.target.value, hex))}
          fullWidth
          sx={catchyTextFieldSx}
          inputProps={{ maxLength: 7, spellCheck: false }}
        />
      </Box>
      {helperText && (
        <Typography fontSize={11} color="text.secondary" mt={0.5}>{helperText}</Typography>
      )}
    </Box>
  );
};

export const Settings: React.FC = () => {
  const { refreshUser, patchCompany, hasPermission } = useAuth();
  const { previewTheme } = useAppTheme();
  const canEdit = hasPermission('settings', 'edit');
  const {
    t, applyLocale,
  } = useLocale();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [themeReady, setThemeReady] = useState(false);
  const [form, setForm] = useState({
    companyName: '',
    subdomain: '',
    tenantUrl: '',
    companyLogo: '',
    invoiceLogo: '',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    sidebarColor: '#1a237e',
    accentColor: '#42a5f5',
    tableColor: '#1a237e',
    formHeaderColor: '#1976d2',
    gstNumber: '',
    panNumber: '',
    taxLabel: 'GST',
    authorizedSignatory: '',
    invoiceSignature: '',
    bankName: '',
    bankAccount: '',
    bankIfsc: '',
    invoicePrefix: 'INV',
    invoiceSuffix: '',
    taxPercentage: 18,
    footerNote: 'Thank you for your business',
    biltyPrefix: 'BLT',
    biltySuffix: '',
    biltyFiscalYearStartMonth: 4,
    biltyAllowManualNumber: true,
    biltyFooterNote: 'Goods carried at owner\'s risk unless insured.',
    language: 'en',
    currency: 'INR',
    timeZone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    country: 'IN',
  });

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const c = res.data || {};
      const theme = c.theme || {};
      const primary = toHexColor(theme.primaryColor, HEX_FALLBACK.primaryColor);
      setForm({
        companyName: c.companyName || '',
        subdomain: c.subdomain || '',
        tenantUrl: c.subdomain ? platformTenantUrl(c.subdomain) : '',
        companyLogo: c.companyLogo || '',
        invoiceLogo: c.invoiceLogo || '',
        primaryColor: primary,
        secondaryColor: toHexColor(theme.secondaryColor, HEX_FALLBACK.secondaryColor),
        sidebarColor: toHexColor(theme.sidebarColor, HEX_FALLBACK.sidebarColor),
        accentColor: toHexColor(theme.accentColor, HEX_FALLBACK.accentColor),
        tableColor: toHexColor(theme.tableColor || theme.primaryColor, HEX_FALLBACK.tableColor),
        formHeaderColor: toHexColor(theme.formHeaderColor || theme.primaryColor, HEX_FALLBACK.formHeaderColor),
        gstNumber: c.gstNumber || '',
        panNumber: c.panNumber || '',
        taxLabel: c.invoiceSettings?.taxLabel || 'GST',
        authorizedSignatory: c.invoiceSettings?.authorizedSignatory || '',
        invoiceSignature: c.invoiceSettings?.invoiceSignature || '',
        bankName: c.invoiceSettings?.bankName || '',
        bankAccount: c.invoiceSettings?.bankAccount || '',
        bankIfsc: c.invoiceSettings?.bankIfsc || '',
        invoicePrefix: c.invoiceSettings?.prefix || 'INV',
        invoiceSuffix: c.invoiceSettings?.suffix || '',
        taxPercentage: c.invoiceSettings?.taxPercentage || 18,
        footerNote: c.invoiceSettings?.footerNote || '',
        biltyPrefix: c.biltySettings?.prefix || 'BLT',
        biltySuffix: c.biltySettings?.suffix || '',
        biltyFiscalYearStartMonth: c.biltySettings?.fiscalYearStartMonth ?? 4,
        biltyAllowManualNumber: c.biltySettings?.allowManualNumber !== false,
        biltyFooterNote: c.biltySettings?.footerNote || 'Goods carried at owner\'s risk unless insured.',
        language: c.language || 'en',
        currency: c.currency || 'INR',
        timeZone: c.timeZone || 'Asia/Kolkata',
        dateFormat: c.dateFormat || 'DD/MM/YYYY',
        country: normalizeCountryCode(c.country || 'IN'),
      });
      setError('');
      setThemeReady(true);
    }).catch((err) => setError(apiErrorMessage(err, 'Failed to load settings')));
  }, []);

  useEffect(() => {
    if (!themeReady) return;
    previewTheme({
      primaryColor: form.primaryColor,
      secondaryColor: form.secondaryColor,
      sidebarColor: form.sidebarColor,
      accentColor: form.accentColor,
      tableColor: form.tableColor,
      formHeaderColor: form.formHeaderColor,
    });
  }, [
    themeReady, previewTheme,
    form.primaryColor, form.secondaryColor, form.sidebarColor,
    form.accentColor, form.tableColor, form.formHeaderColor,
  ]);

  useEffect(() => () => previewTheme(null), [previewTheme]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const countryCode = normalizeCountryCode(form.country);
      const res = await settingsAPI.update({
        companyName: form.companyName,
        subdomain: form.subdomain,
        companyLogo: form.companyLogo,
        invoiceLogo: form.invoiceLogo,
        language: form.language,
        currency: form.currency,
        timeZone: form.timeZone,
        dateFormat: form.dateFormat,
        country: countryCode,
        theme: {
          primaryColor: toHexColor(form.primaryColor, HEX_FALLBACK.primaryColor),
          secondaryColor: toHexColor(form.secondaryColor, HEX_FALLBACK.secondaryColor),
          sidebarColor: toHexColor(form.sidebarColor, HEX_FALLBACK.sidebarColor),
          accentColor: toHexColor(form.accentColor, HEX_FALLBACK.accentColor),
          tableColor: toHexColor(form.tableColor, HEX_FALLBACK.tableColor),
          formHeaderColor: toHexColor(form.formHeaderColor, HEX_FALLBACK.formHeaderColor),
        },
        invoiceSettings: {
          prefix: form.invoicePrefix,
          suffix: form.invoiceSuffix,
          taxPercentage: Number(form.taxPercentage),
          taxLabel: form.taxLabel,
          footerNote: form.footerNote,
          authorizedSignatory: form.authorizedSignatory,
          invoiceSignature: form.invoiceSignature,
          bankName: form.bankName,
          bankAccount: form.bankAccount,
          bankIfsc: form.bankIfsc,
        },
        gstNumber: form.gstNumber,
        panNumber: form.panNumber,
        biltySettings: {
          prefix: form.biltyPrefix,
          suffix: form.biltySuffix,
          fiscalYearStartMonth: Number(form.biltyFiscalYearStartMonth) || 4,
          allowManualNumber: form.biltyAllowManualNumber,
          footerNote: form.biltyFooterNote,
        },
      });
      if (res.data?.company) {
        const saved = res.data.company;
        patchCompany({
          companyName: saved.companyName,
          companyLogo: saved.companyLogo,
          invoiceLogo: saved.invoiceLogo,
          subdomain: saved.subdomain,
          theme: saved.theme,
          language: saved.language,
          currency: saved.currency,
          timeZone: saved.timeZone,
          dateFormat: saved.dateFormat,
          country: normalizeCountryCode(saved.country),
        });
        setForm((f) => ({
          ...f,
          language: saved.language || f.language,
          currency: saved.currency || f.currency,
          timeZone: saved.timeZone || f.timeZone,
          dateFormat: saved.dateFormat || f.dateFormat,
          country: normalizeCountryCode(saved.country || f.country),
          ...(saved.subdomain
            ? {
              subdomain: saved.subdomain,
              tenantUrl: res.data.tenantUrl || platformTenantUrl(saved.subdomain),
            }
            : {}),
        }));
      }
      applyLocale({
        language: form.language,
        currency: form.currency,
        timezone: form.timeZone,
        dateFormat: form.dateFormat,
        country: countryCode,
      });
      await refreshUser();
      const successByTab = [
        'Theme saved. Sidebar, tables, and form headers now use your colors.',
        'Regional settings saved. Currency, dates, and timezone now apply across the app.',
        'Invoice settings saved.',
        'Bilty settings saved.',
        'Settings saved successfully.',
      ];
      setMessage(successByTab[tab] || 'Settings saved successfully.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save settings'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Company Settings"
        icon={<SettingsIcon />}
      />
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ px: 2 }}
        >
          <Tab label="Theme" icon={<PaletteIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Regional" icon={<LanguageIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Invoice" />
          <Tab label="Bilty" />
          <Tab label="Data Tables" icon={<TableChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
        <Divider />
        <CardContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2,
          maxWidth: tab === 0 || tab === 4 ? '100%' : 720,
          px: { xs: 2, sm: 3 },
        }}>
          {tab === 0 && (
            <>
              <Typography fontSize={13} color="text.secondary">
                Pick your company colors. The sidebar, data-table headers, and Add/Edit form bars update as you change them. Click Save to keep the theme.
              </Typography>
              <ThemePresetPicker
                disabled={!canEdit}
                active={{
                  sidebarColor: form.sidebarColor,
                  tableColor: form.tableColor,
                  formHeaderColor: form.formHeaderColor,
                  primaryColor: form.primaryColor,
                }}
                onSelect={(preset) => setForm((f) => ({
                  ...f,
                  sidebarColor: preset.sidebarColor,
                  tableColor: preset.tableColor,
                  formHeaderColor: preset.formHeaderColor,
                  primaryColor: preset.primaryColor,
                  secondaryColor: preset.secondaryColor,
                  accentColor: preset.accentColor,
                }))}
              />
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography fontWeight={800} fontSize={14}>Company colors</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label="Sidebar Color" value={form.sidebarColor}
                    onChange={(sidebarColor) => setForm({ ...form, sidebarColor })}
                    helperText="Left menu background" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label="Data Table Color" value={form.tableColor}
                    onChange={(tableColor) => setForm({ ...form, tableColor })}
                    helperText="Table header row" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label="Form Header Color" value={form.formHeaderColor}
                    onChange={(formHeaderColor) => setForm({ ...form, formHeaderColor })}
                    helperText="Add / Edit form top bar" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label="Primary / Buttons" value={form.primaryColor}
                    onChange={(primaryColor) => setForm({ ...form, primaryColor })}
                    helperText="Buttons and highlights" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label="Secondary Color" value={form.secondaryColor}
                    onChange={(secondaryColor) => setForm({ ...form, secondaryColor })} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField label="Accent Color" value={form.accentColor}
                    onChange={(accentColor) => setForm({ ...form, accentColor })} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {[
                      { label: 'Sidebar', color: form.sidebarColor },
                      { label: 'Table header', color: form.tableColor },
                      { label: 'Form header', color: form.formHeaderColor },
                      { label: 'Buttons', color: form.primaryColor },
                    ].map((swatch) => (
                      <Box key={swatch.label} sx={{
                        flex: 1, minWidth: 120, borderRadius: 1.5, overflow: 'hidden',
                        border: '1px solid #e2e8f0',
                      }}>
                        <Box sx={{ height: 40, bgcolor: swatch.color }} />
                        <Typography fontSize={11} fontWeight={700} sx={{ px: 1, py: 0.75 }}>{swatch.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ImageUploadField label="Company Logo" value={form.companyLogo}
                    onUpload={async (file) => {
                      const fd = new FormData(); fd.append('file', file); fd.append('type', 'logo');
                      const res = await settingsAPI.uploadBranding(fd);
                      await refreshUser();
                      return res.data.url;
                    }}
                    onChange={(url) => setForm({ ...form, companyLogo: url })}
                    helperText="Shown in the tenant sidebar. PNG or JPG, transparent background works best." />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ImageUploadField label="Invoice Logo" value={form.invoiceLogo}
                    onUpload={async (file) => {
                      const fd = new FormData(); fd.append('file', file); fd.append('type', 'invoiceLogo');
                      const res = await settingsAPI.uploadBranding(fd); return res.data.url;
                    }}
                    onChange={(url) => setForm({ ...form, invoiceLogo: url })}
                    helperText="Shown on invoice / LR print" />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Company Name" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} fullWidth sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Portal Subdomain"
                    value={form.subdomain}
                    onChange={(e) => setForm({
                      ...form,
                      subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      tenantUrl: e.target.value ? platformTenantUrl(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')) : '',
                    })}
                    fullWidth
                    sx={catchyTextFieldSx}
                    helperText="Your tenant URL — custom domain can be linked later"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Tenant URL"
                    value={form.tenantUrl || (form.subdomain ? platformTenantUrl(form.subdomain) : '')}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={catchyTextFieldSx}
                    helperText={`e.g. yourcompany.${PLATFORM_DOMAIN}`}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {tab === 1 && (
            <RegionalSettingsPanel
              variant="tenant"
              values={{
                language: form.language,
                currency: form.currency,
                timezone: form.timeZone,
                dateFormat: form.dateFormat,
                country: form.country,
              }}
              onChange={(patch) => setForm((f) => ({
                ...f,
                ...(patch.language !== undefined && { language: patch.language }),
                ...(patch.currency !== undefined && { currency: patch.currency }),
                ...(patch.timezone !== undefined && { timeZone: patch.timezone }),
                ...(patch.dateFormat !== undefined && { dateFormat: patch.dateFormat }),
                ...(patch.country !== undefined && { country: patch.country }),
              }))}
              labels={{
                title: t('regionalSettings'),
                language: t('language'),
                currency: t('currency'),
                timezone: t('timezone'),
                dateFormat: t('dateFormat'),
                country: t('country'),
              }}
            />
          )}
          {tab === 2 && (
            <>
              <Typography fontSize={13} color="text.secondary" mb={1}>
                Invoice branding matches platform structure: logo, GST/PAN, tax, bank details & signature.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="GST Number" value={form.gstNumber}
                    onChange={(e) => setForm({ ...form, gstNumber: e.target.value })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="PAN Number" value={form.panNumber}
                    onChange={(e) => setForm({ ...form, panNumber: e.target.value })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Tax Label" value={form.taxLabel}
                    onChange={(e) => setForm({ ...form, taxLabel: e.target.value })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Default Tax %" value={form.taxPercentage}
                    onChange={(e) => setForm({ ...form, taxPercentage: Number(e.target.value) })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Bank Name" value={form.bankName}
                    onChange={(e) => setForm({ ...form, bankName: e.target.value })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Account Number" value={form.bankAccount}
                    onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="IFSC" value={form.bankIfsc}
                    onChange={(e) => setForm({ ...form, bankIfsc: e.target.value })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <ImageUploadField label="Authorized Signature" value={form.invoiceSignature}
                    onUpload={async (file) => {
                      const fd = new FormData(); fd.append('file', file); fd.append('type', 'signature');
                      const res = await settingsAPI.uploadBranding(fd); return res.data.url;
                    }}
                    onChange={(url) => setForm({ ...form, invoiceSignature: url })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Signatory Name" value={form.authorizedSignatory}
                    onChange={(e) => setForm({ ...form, authorizedSignatory: e.target.value })} sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Invoice Prefix" value={form.invoicePrefix} onChange={(e) => setForm({ ...form, invoicePrefix: e.target.value })} fullWidth sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Invoice Suffix" value={form.invoiceSuffix} onChange={(e) => setForm({ ...form, invoiceSuffix: e.target.value })} fullWidth sx={catchyTextFieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Footer Note" value={form.footerNote} onChange={(e) => setForm({ ...form, footerNote: e.target.value })} fullWidth multiline rows={2} sx={catchyTextFieldSx} />
                </Grid>
              </Grid>
            </>
          )}
          {tab === 3 && (
            <>
              <TextField label="Bilty Prefix" value={form.biltyPrefix} onChange={(e) => setForm({ ...form, biltyPrefix: e.target.value })} fullWidth sx={catchyTextFieldSx} />
              <TextField label="Bilty Suffix" value={form.biltySuffix} onChange={(e) => setForm({ ...form, biltySuffix: e.target.value })} fullWidth sx={catchyTextFieldSx} />
              <TextField select label="Fiscal Year Starts (Month)" value={form.biltyFiscalYearStartMonth}
                onChange={(e) => setForm({ ...form, biltyFiscalYearStartMonth: Number(e.target.value) })}
                fullWidth sx={catchyTextFieldSx}>
                {[
                  { v: 1, l: 'January (Calendar Year)' },
                  { v: 4, l: 'April (India FY)' },
                  { v: 7, l: 'July' },
                  { v: 10, l: 'October' },
                ].map((m) => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
              </TextField>
              <FormControlLabel
                control={<Switch checked={form.biltyAllowManualNumber} onChange={(e) => setForm({ ...form, biltyAllowManualNumber: e.target.checked })} />}
                label="Allow manual bilty number entry"
              />
              <TextField label="Bilty Footer Note" value={form.biltyFooterNote} onChange={(e) => setForm({ ...form, biltyFooterNote: e.target.value })}
                fullWidth multiline rows={2} sx={catchyTextFieldSx} />
              <Typography variant="caption" color="text.secondary">
                Auto numbers use format: PREFIX-FY2025-26-00001. Sequence resets each fiscal year.
              </Typography>
            </>
          )}
          {tab === 4 && <TableColumnsSettings />}
          {tab !== 4 && (
          <>
          <Button variant="contained" onClick={handleSave} disabled={loading || !canEdit} sx={{
            ...catchyPrimaryButtonSx,
            alignSelf: { xs: 'stretch', sm: 'flex-start' },
            width: { xs: '100%', sm: 'auto' },
          }}>
            Save Settings
          </Button>
          {!canEdit && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              You have view-only access to settings. Contact your admin for edit permission.
            </Typography>
          )}
          </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
